<template>
	<section>
		<AsiPageTitle :icon="icons.order" :title="title" :loading="loading">
			<template v-slot:subtitle>
				<AsiBreadcrumbs :entries="path" hide-icons link-entries/>
			</template>

			<template v-slot:fixed-actions>
				<div class="d-flex flex-row gap-1 pr-4">
					<AsiBtn text :icon="icons.paid" :disabled="!showPaidButton || isOrderCanceled"
					        @click="setOrderPaid" >
						{{ $t('order.terms.paid') }}
					</AsiBtn>
					<AsiBtn text :icon="icons.ban" :disabled="isOrderCanceled" color="error"
					        @click="setOrderCanceled">
						{{ $t('order.terms.cancel') }}
					</AsiBtn>
				</div>
			</template>
		</AsiPageTitle>



		<OrderTabDetails :order="order" @change="loadOrder"/>

		<AsiConfirmDialog ref="confirm"/>

	</section>
</template>

<script lang="ts">
	import {Component, Prop, Watch} from "vue-property-decorator";
	import AsiPageTitle from "@/components/common/AsiPageTitle.vue";
	import Icon from "@/plugins/icons";
	import {IOrderAdmin} from "@/models/order/OrderAdminModels";
	import Snackbar from "@/helpers/Snackbar";
	import VueI18n from "vue-i18n";
	import AsiBreadcrumbs from "@/components/common/AsiBreadcrumbs.vue";
	import PathEntry from "@/models/PathEntry";
	import CustomerHelper from "@/models/customer/CustomerHelper";
	import OrderTabDetails from "@/components/order/admin/OrderTabDetails.vue";
	import TranslateResult = VueI18n.TranslateResult;
	import AsiMenuItem from "@/components/common/AsiMenuItem.vue";
	import AsiConfirmDialog from "@/components/common/AsiConfirmDialog.vue";
	import {OrderPaymentState, OrderShipmentState} from "@/helpers/constants";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import {mixins} from "vue-class-component";
	import RightChecks from "@/mixins/RightChecks.vue";

	@Component({
		components: {AsiBtn, AsiConfirmDialog, AsiMenuItem, OrderTabDetails, AsiBreadcrumbs, AsiPageTitle}
	})
	export default class Order extends mixins(RightChecks) {

		@Prop({type: String, required: true})
		public id!: string;

		public icons = Icon;
		public loading: boolean = false;
		public order: IOrderAdmin | null = null;

		public get title(): string | null {
			const parts: (string | TranslateResult)[] = [this.$t('order.singular')];
			if (this.order !== null) {
				parts.push(this.order.orderNumber.toString());
			}
			return parts.join(' ');
		}

		public get path(): PathEntry[] {
			return this.order === null || this.order.customer === null
				? []
				: [CustomerHelper.pathEntry(this.order.customer, true)];
		}

		@Watch('id', {immediate: true})
		private onIdChanged(): void {
			this.loadOrder();
		}

		public loadOrder(): void {
			if (this.id) {
				this.loading = true;
				this.$orderServiceAdmin.order(this.id)
					.then(model => this.order = model)
					.catch(() => Snackbar.loadingError())
					.finally(() => this.loading = false);
			}
		}

		public get isOrderCanceled(): boolean {
			return this.order?.shipmentState === OrderShipmentState.orderShipmentCanceled
				|| this.order?.shipmentState === OrderShipmentState.orderShipmentShipped;
		}

		private get showPaidButton(): boolean {
			return this.rFinance
				&& this.order?.paymentType.paymentProviderCode === null
				&& this.order.paymentState === OrderPaymentState.orderPaymentUnpaid;
		}

		private setOrderPaid(): void {
			const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
			confirm.openDialog().then((res: boolean) => {
				if (!res) return;
				this.loading = true;
				if (this.order !== null) {
					this.$orderServiceAdmin.paid(this.order.id)
						.then(() => this.loadOrder())
						.catch(() => Snackbar.deleteError())
						.finally(() => this.loading = false);
				}
			});
		}

		public setOrderCanceled(): void {
			const confirm = this.$refs.confirm as unknown as AsiConfirmDialog;
			confirm.openDialog().then((res: boolean) => {
				if (!res) return;
				this.loading = true;
				if (this.order !== null) {
					this.$orderServiceAdmin.cancel(this.order.id)
						.then(() => this.loadOrder())
						.catch(() => Snackbar.deleteError())
						.finally(() => this.loading = false);
				}
			});
		}

	}
</script>

<style lang="scss" scoped>
	.gap-1 {
		gap: 1rem
	}
</style>
